/**
 * CRAD FDIC Large Bank — detail page
 *
 * Per CLARID-SPEC-CRAD-LARGE-001 §8. Tabs:
 *  - Overview            review period, bank info, status
 *  - Institution Ratings 4-tier institution + 5-tier per-test + per-state
 *  - Component Ratings   point allocation table, composite, recommendations
 *  - Peer Comparison     2-3 named peers, manual entry at MVP
 *
 * Phase A ships these four tabs. Per-AA detail, workbook export with full
 * tab parity, and Memorandum generation arrive in Phase B and C.
 *
 * Every rating is editable via the Component Ratings tab. The PATCH route
 * recomputes the composite + institution rating whenever all three test
 * ratings are present.
 */

import { useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tab } from '@headlessui/react';
import {
  CpuChipIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  TrashIcon,
  ScaleIcon,
  ChevronLeftIcon,
  SparklesIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { cradLargeBankApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { PageHeader, StatusBadge, LoadingSpinner } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

type TestRating =
  | 'OUTSTANDING'
  | 'HIGH_SATISFACTORY'
  | 'LOW_SATISFACTORY'
  | 'NEEDS_TO_IMPROVE'
  | 'SUBSTANTIAL_NONCOMPLIANCE';

type InstitutionRating =
  | 'OUTSTANDING'
  | 'SATISFACTORY'
  | 'NEEDS_TO_IMPROVE'
  | 'SUBSTANTIAL_NONCOMPLIANCE';

const TEST_RATING_LABEL: Record<TestRating, string> = {
  OUTSTANDING: 'Outstanding',
  HIGH_SATISFACTORY: 'High Satisfactory',
  LOW_SATISFACTORY: 'Low Satisfactory',
  NEEDS_TO_IMPROVE: 'Needs to Improve',
  SUBSTANTIAL_NONCOMPLIANCE: 'Substantial Noncompliance',
};

const TEST_RATING_OPTIONS: TestRating[] = [
  'OUTSTANDING',
  'HIGH_SATISFACTORY',
  'LOW_SATISFACTORY',
  'NEEDS_TO_IMPROVE',
  'SUBSTANTIAL_NONCOMPLIANCE',
];

const TEST_RATING_BADGE: Record<TestRating, BadgeVariant> = {
  OUTSTANDING: 'success',
  HIGH_SATISFACTORY: 'success',
  LOW_SATISFACTORY: 'info',
  NEEDS_TO_IMPROVE: 'warning',
  SUBSTANTIAL_NONCOMPLIANCE: 'danger',
};

const INSTITUTION_LABEL: Record<InstitutionRating, string> = {
  OUTSTANDING: 'Outstanding',
  SATISFACTORY: 'Satisfactory',
  NEEDS_TO_IMPROVE: 'Needs to Improve',
  SUBSTANTIAL_NONCOMPLIANCE: 'Substantial Noncompliance',
};

const INSTITUTION_BADGE: Record<InstitutionRating, BadgeVariant> = {
  OUTSTANDING: 'success',
  SATISFACTORY: 'info',
  NEEDS_TO_IMPROVE: 'warning',
  SUBSTANTIAL_NONCOMPLIANCE: 'danger',
};

/** Points table from spec §6.4 — also reflected in scoring service. */
const POINT_TABLE: Record<TestRating, { lending: number; investment: number; service: number }> = {
  OUTSTANDING: { lending: 12, investment: 6, service: 6 },
  HIGH_SATISFACTORY: { lending: 9, investment: 4, service: 4 },
  LOW_SATISFACTORY: { lending: 6, investment: 3, service: 3 },
  NEEDS_TO_IMPROVE: { lending: 3, investment: 1, service: 1 },
  SUBSTANTIAL_NONCOMPLIANCE: { lending: 0, investment: 0, service: 0 },
};

const COMPOSITE_BANDS: Array<{ min: number; rating: InstitutionRating; label: string }> = [
  { min: 20, rating: 'OUTSTANDING', label: '≥ 20' },
  { min: 11, rating: 'SATISFACTORY', label: '11 – 19' },
  { min: 5, rating: 'NEEDS_TO_IMPROVE', label: '5 – 10' },
  { min: 0, rating: 'SUBSTANTIAL_NONCOMPLIANCE', label: '0 – 4' },
];

interface StateRating {
  id: string;
  stateName: string;
  isMultistateMSA: boolean;
  msaCode: string | null;
  lendingTestRating: TestRating | null;
  investmentTestRating: TestRating | null;
  serviceTestRating: TestRating | null;
  totalStateDeposits: number | null;
  stateDepositSharePct: number | null;
  notes: string | null;
}

interface PeerBenchmark {
  id: string;
  peerName: string;
  peerCertNumber: number | null;
  peerExamDate: string | null;
  peerAssetSize: number | null;
  cdLoansSummary: string | null;
  qualifiedInvestmentsSummary: string | null;
  cdServicesSummary: string | null;
  overallConclusion: string | null;
}

interface AssessmentArea {
  id: string;
  name: string;
  description: string | null;
  stateName: string | null;
  multistateMsaName: string | null;
  msaCodes: string[];
  countyCodes: string[];
  totalTracts: number;
  lmiTracts: number;
  majorityMinority: number;
  totalPopulation: number;
  lmiPopulationPct: number | null;
  minorityPopulationPct: number | null;
  medianFamilyIncome: number | null;
  medianFamilyIncomeYear: number | null;
  housingUnits: number | null;
  ownerOccupiedUnits: number | null;
  rentalUnits: number | null;
  vacantUnits: number | null;
  populationByIncome: Record<string, { count?: number; pct?: number }> | null;
  housingByIncome: Record<string, { count?: number; pct?: number }> | null;
  mmctMethodology: string | null;
  referenceYear: number;
}

interface QualifiedInvestment {
  id: string;
  assessmentAreaId: string;
  assessmentArea?: { name: string; stateName: string | null; multistateMsaName: string | null } | null;
  currentInvestmentCount: number;
  currentInvestmentAmount: number;
  priorInvestmentCount: number;
  priorInvestmentAmount: number;
  grantsCount: number;
  grantsAmount: number;
  qiAsPctTotalAssets: number | null;
  qiAsPctTotalSecurities: number | null;
  conclusion: string | null;
  rating: TestRating | null;
}

interface TestResult {
  id: string;
  assessmentAreaId: string;
  assessmentArea?: { name: string; stateName: string | null; multistateMsaName: string | null } | null;
  test: 'LENDING' | 'INVESTMENT' | 'SERVICE';
  reviewScope: string;
  year: number;
  subCriterion: string;
  conclusion: string | null;
  rating: TestRating | null;
  metadata: Record<string, unknown> | null;
}

interface IfSuggestion {
  id: string;
  programName: string;
  programCategory: string | null;
  rationale: string;
  proposedRating: TestRating | null;
  confidence: number;
  sourceRef: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'EDITED' | 'REJECTED';
  editedName: string | null;
  editedRationale: string | null;
  analystNotes: string | null;
}

interface Recommendation {
  id: string;
  sequence: number;
  text: string;
}

interface LargeBankReport {
  id: string;
  bankName: string;
  fdicCertNumber: number | null;
  primaryRegulator: string;
  startDate: string;
  endDate: string;
  priorExamDate: string | null;
  status: string;
  computedAt: string | null;
  memorandumText: string | null;
  memorandumGeneratedAt: string | null;
  lendingTestRating: TestRating | null;
  investmentTestRating: TestRating | null;
  serviceTestRating: TestRating | null;
  lendingPoints: number | null;
  investmentPoints: number | null;
  servicePoints: number | null;
  compositePoints: number | null;
  institutionRating: InstitutionRating | null;
  notes: string | null;
  stateRatings: StateRating[];
  peerBenchmarks: PeerBenchmark[];
  recommendations: Recommendation[];
  qualifiedInvestments: QualifiedInvestment[];
  testResults: TestResult[];
  ifSuggestions: IfSuggestion[];
  assessmentAreas: AssessmentArea[];
}

export default function CradLargeBankPage() {
  const { id } = useParams<{ id: string }>();
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['crad-large-bank', teamId, id],
    queryFn: () => cradLargeBankApi.get(teamId!, id!),
    enabled: !!teamId && !!id,
  });
  const report: LargeBankReport | undefined = data?.data;

  const updateMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) => cradLargeBankApi.update(teamId!, id!, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, id] });
    },
    onError: () => toast.error('Failed to save'),
  });

  const computeMutation = useMutation({
    mutationFn: () => cradLargeBankApi.compute(teamId!, id!),
    onSuccess: () => {
      toast.success('Composite recomputed');
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, id] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Failed to compute';
      toast.error(msg);
    },
  });

  if (!teamId || !id) return <LoadingSpinner size="lg" />;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (!report) {
    return (
      <div className="py-16 text-center">
        <p className="text-gray-500">Large Bank report not found.</p>
        <Link to="/cra/large-bank" className="mt-4 inline-flex items-center text-teal-700 hover:text-teal-900">
          <ChevronLeftIcon className="h-4 w-4 mr-1" />
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <FeatureGate feature="module_cra">
      <div className="space-y-6">
        <PageHeader
          title={`Large Bank CRAD — ${report.bankName}`}
          description={`${report.primaryRegulator} · ${new Date(report.startDate).toLocaleDateString()} – ${new Date(
            report.endDate,
          ).toLocaleDateString()}`}
          actions={
            <div className="flex items-center gap-2">
              <Link
                to="/cra/large-bank"
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Back
              </Link>
              <a
                href={cradLargeBankApi.workbookExportUrl(teamId, id)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download workbook
              </a>
              <button
                onClick={() => computeMutation.mutate()}
                disabled={computeMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
              >
                <CpuChipIcon className="h-4 w-4" />
                {computeMutation.isPending ? 'Computing…' : 'Compute composite'}
              </button>
            </div>
          }
        />

        <InstitutionSummaryCard report={report} />

        <Tab.Group>
          <Tab.List className="flex space-x-1 rounded-lg bg-gray-100 p-1">
            {[
              'Overview',
              'Institution Ratings',
              'Component & Composite',
              'Lending Test',
              'Investment Test',
              'Service Test',
              'Per-AA Detail',
              'Peer Comparison',
              'Memorandum',
            ].map((label) => (
              <Tab
                key={label}
                className={({ selected }) =>
                  clsx(
                    'w-full rounded-md py-2 text-sm font-medium',
                    selected
                      ? 'bg-white text-teal-700 shadow'
                      : 'text-gray-600 hover:bg-white/60',
                  )
                }
              >
                {label}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="mt-4">
            <Tab.Panel>
              <OverviewPanel report={report} />
            </Tab.Panel>
            <Tab.Panel>
              <InstitutionRatingsPanel report={report} />
            </Tab.Panel>
            <Tab.Panel>
              <ComponentRatingsPanel
                report={report}
                onPatch={(patch) => updateMutation.mutate(patch)}
                isSaving={updateMutation.isPending}
              />
            </Tab.Panel>
            <Tab.Panel>
              <LendingTestPanel report={report} />
            </Tab.Panel>
            <Tab.Panel>
              <InvestmentTestPanel report={report} />
            </Tab.Panel>
            <Tab.Panel>
              <ServiceTestPanel report={report} />
            </Tab.Panel>
            <Tab.Panel>
              <PerAaDetailPanel report={report} />
            </Tab.Panel>
            <Tab.Panel>
              <PeerComparisonPanel report={report} />
            </Tab.Panel>
            <Tab.Panel>
              <MemorandumPanel report={report} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </FeatureGate>
  );
}

// ── Cards / panels ──────────────────────────────────────────────────────────

function InstitutionSummaryCard({ report }: { report: LargeBankReport }) {
  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">Preliminary Institution Rating</p>
          <div className="mt-2">
            {report.institutionRating ? (
              <StatusBadge variant={INSTITUTION_BADGE[report.institutionRating]}>
                {INSTITUTION_LABEL[report.institutionRating]}
              </StatusBadge>
            ) : (
              <span className="text-sm text-gray-400">Not yet computed</span>
            )}
          </div>
          {report.compositePoints != null && (
            <p className="mt-2 text-xs text-gray-500">
              Composite score: <span className="font-medium">{report.compositePoints} points</span>
            </p>
          )}
        </div>
        <div className="grid grid-cols-3 gap-6 text-center">
          {(['LENDING', 'INVESTMENT', 'SERVICE'] as const).map((test) => {
            const rating =
              test === 'LENDING'
                ? report.lendingTestRating
                : test === 'INVESTMENT'
                  ? report.investmentTestRating
                  : report.serviceTestRating;
            const pts =
              test === 'LENDING'
                ? report.lendingPoints
                : test === 'INVESTMENT'
                  ? report.investmentPoints
                  : report.servicePoints;
            return (
              <div key={test}>
                <p className="text-xs uppercase tracking-wide text-gray-400">{test} test</p>
                <div className="mt-1">
                  {rating ? (
                    <StatusBadge variant={TEST_RATING_BADGE[rating]}>{TEST_RATING_LABEL[rating]}</StatusBadge>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </div>
                {pts != null && <p className="mt-1 text-xs text-gray-500">{pts} pts</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function OverviewPanel({ report }: { report: LargeBankReport }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900">Engagement</h3>
        <dl className="mt-3 space-y-2 text-sm">
          <Row label="Bank" value={report.bankName} />
          <Row label="FDIC Cert #" value={report.fdicCertNumber ?? '—'} />
          <Row label="Regulator" value={report.primaryRegulator} />
          <Row
            label="Review period"
            value={`${new Date(report.startDate).toLocaleDateString()} – ${new Date(
              report.endDate,
            ).toLocaleDateString()}`}
          />
          <Row
            label="Prior exam"
            value={report.priorExamDate ? new Date(report.priorExamDate).toLocaleDateString() : '—'}
          />
          <Row label="Status" value={report.status} />
          <Row
            label="Computed at"
            value={report.computedAt ? new Date(report.computedAt).toLocaleString() : '—'}
          />
        </dl>
      </div>
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900">Methodology</h3>
        <p className="mt-3 text-sm text-gray-600">
          Three-test evaluation per FDIC Large Bank procedures: Lending, Investment, and Service. Each
          test is rated on a 5-tier scale (Outstanding · High Satisfactory · Low Satisfactory · NTI · SN)
          and assigned points. The composite point total reduces to a 4-tier institution rating
          (≥ 20 Outstanding · 11 – 19 Satisfactory · 5 – 10 NTI · 0 – 4 SN).
        </p>
        <p className="mt-3 text-sm text-gray-600">
          State and multistate-MSA ratings are computed via deposit-weighted majority across AAs, with
          analyst override available at every level (AA → state → test → institution).
        </p>
      </div>
    </div>
  );
}

function InstitutionRatingsPanel({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [addingState, setAddingState] = useState(false);
  const [newStateName, setNewStateName] = useState('');
  const [newIsMultistate, setNewIsMultistate] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });

  const updateMutation = useMutation({
    mutationFn: ({ stateRatingId, body }: { stateRatingId: string; body: Record<string, unknown> }) =>
      cradLargeBankApi.updateStateRating(teamId!, report.id, stateRatingId, body),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to update state rating'),
  });

  const addMutation = useMutation({
    mutationFn: (body: { stateName: string; isMultistateMSA?: boolean }) =>
      cradLargeBankApi.addStateRating(teamId!, report.id, body),
    onSuccess: () => {
      invalidate();
      setAddingState(false);
      setNewStateName('');
      setNewIsMultistate(false);
    },
    onError: () => toast.error('Failed to add state rating'),
  });

  const deleteMutation = useMutation({
    mutationFn: (stateRatingId: string) =>
      cradLargeBankApi.deleteStateRating(teamId!, report.id, stateRatingId),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to delete state rating'),
  });

  const recomputeMutation = useMutation({
    mutationFn: () => cradLargeBankApi.recomputeStateRollup(teamId!, report.id),
    onSuccess: () => {
      toast.success('State rollup recomputed');
      invalidate();
    },
    onError: () => toast.error('Failed to recompute state rollup'),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">State / Multistate-MSA Ratings</h3>
            <p className="text-xs text-gray-500">
              Deposit-weighted rollup per spec §6.5. Click any rating to override. "Recompute"
              rebuilds state ratings from current AA-level test results.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => recomputeMutation.mutate()}
              disabled={recomputeMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
            >
              <CpuChipIcon className="h-4 w-4" />
              {recomputeMutation.isPending ? 'Recomputing…' : 'Recompute'}
            </button>
            <button
              onClick={() => setAddingState((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4" />
              Add state
            </button>
          </div>
        </div>

        {addingState && (
          <div className="border-b px-6 py-3 bg-gray-50">
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={newStateName}
                onChange={(e) => setNewStateName(e.target.value)}
                placeholder="State or multistate MSA name"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
              />
              <label className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newIsMultistate}
                  onChange={(e) => setNewIsMultistate(e.target.checked)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span>Multistate MSA</span>
              </label>
              <button
                onClick={() =>
                  newStateName.trim() &&
                  addMutation.mutate({
                    stateName: newStateName.trim(),
                    isMultistateMSA: newIsMultistate,
                  })
                }
                disabled={!newStateName.trim() || addMutation.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
              >
                Add
              </button>
              <button
                onClick={() => setAddingState(false)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {report.stateRatings.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            <ScaleIcon className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-2">
              No state-level ratings yet. Run a test compute (Lending / Investment / Service) or
              click "Recompute" once your AAs are populated.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deposit Share
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lending
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Investment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {report.stateRatings.map((s) => (
                <tr key={s.id}>
                  <td className="px-6 py-3 text-sm align-top">
                    <p className="font-medium text-gray-900">{s.stateName}</p>
                    {s.isMultistateMSA && (
                      <p className="text-xs text-gray-500">
                        Multistate MSA{s.msaCode ? ` (${s.msaCode})` : ''}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700 align-top whitespace-nowrap">
                    {s.stateDepositSharePct != null
                      ? `${(s.stateDepositSharePct * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                  <td className="px-6 py-3 align-top">
                    <EditableRating
                      value={s.lendingTestRating}
                      onChange={(v) =>
                        updateMutation.mutate({
                          stateRatingId: s.id,
                          body: { lendingTestRating: v },
                        })
                      }
                    />
                  </td>
                  <td className="px-6 py-3 align-top">
                    <EditableRating
                      value={s.investmentTestRating}
                      onChange={(v) =>
                        updateMutation.mutate({
                          stateRatingId: s.id,
                          body: { investmentTestRating: v },
                        })
                      }
                    />
                  </td>
                  <td className="px-6 py-3 align-top">
                    <EditableRating
                      value={s.serviceTestRating}
                      onChange={(v) =>
                        updateMutation.mutate({
                          stateRatingId: s.id,
                          body: { serviceTestRating: v },
                        })
                      }
                    />
                  </td>
                  <td className="px-6 py-3 align-top">
                    <EditableNotes
                      value={s.notes ?? ''}
                      onSave={(notes) =>
                        updateMutation.mutate({ stateRatingId: s.id, body: { notes } })
                      }
                    />
                  </td>
                  <td className="px-6 py-3 align-top">
                    <button
                      onClick={() => deleteMutation.mutate(s.id)}
                      title="Delete state rating"
                      className="text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Inline rating dropdown — committed on change. */
function EditableRating({
  value,
  onChange,
}: {
  value: TestRating | null;
  onChange: (rating: TestRating | null) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value || null) as TestRating | null)}
      className={clsx(
        'rounded border text-xs px-2 py-1 focus:border-teal-500 focus:ring-teal-500',
        value ? `border-gray-300 bg-white` : 'border-gray-200 bg-gray-50 text-gray-400',
      )}
    >
      <option value="">—</option>
      {TEST_RATING_OPTIONS.map((r) => (
        <option key={r} value={r}>
          {TEST_RATING_LABEL[r]}
        </option>
      ))}
    </select>
  );
}

/** Inline notes editor — click to edit, blur to save. */
function EditableNotes({
  value,
  onSave,
}: {
  value: string;
  onSave: (notes: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <button
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        className={clsx(
          'text-left text-xs max-w-xs truncate',
          value ? 'text-gray-700 hover:text-teal-700' : 'text-gray-400 italic hover:text-teal-700',
        )}
      >
        {value || 'Add notes…'}
      </button>
    );
  }
  return (
    <textarea
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== value) onSave(draft);
        setEditing(false);
      }}
      rows={2}
      className="w-64 rounded border-gray-300 text-xs focus:border-teal-500 focus:ring-teal-500"
    />
  );
}

function ComponentRatingsPanel({
  report,
  onPatch,
  isSaving,
}: {
  report: LargeBankReport;
  onPatch: (patch: Record<string, unknown>) => void;
  isSaving: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900">Per-test ratings (analyst override)</h3>
        <p className="mt-1 text-xs text-gray-500">
          Setting all three drives the composite + institution rating automatically. The per-test
          compute services (Phase B) will populate these from AA-level results.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <RatingSelect
            label="Lending Test"
            value={report.lendingTestRating}
            onChange={(v) => onPatch({ lendingTestRating: v })}
            disabled={isSaving}
          />
          <RatingSelect
            label="Investment Test"
            value={report.investmentTestRating}
            onChange={(v) => onPatch({ investmentTestRating: v })}
            disabled={isSaving}
          />
          <RatingSelect
            label="Service Test"
            value={report.serviceTestRating}
            onChange={(v) => onPatch({ serviceTestRating: v })}
            disabled={isSaving}
          />
        </div>
      </div>

      <PointAllocationCard report={report} />
      <RecommendationsCard report={report} />
    </div>
  );
}

function PointAllocationCard({ report }: { report: LargeBankReport }) {
  return (
    <div className="rounded-lg border bg-white">
      <div className="border-b px-6 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Point Allocation</h3>
        <p className="text-xs text-gray-500">FDIC Large Bank scoring rubric — spec §6.4.</p>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Per-test rating
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Lending
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Investment
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Service
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {TEST_RATING_OPTIONS.map((rating) => {
            const isLending = report.lendingTestRating === rating;
            const isInvestment = report.investmentTestRating === rating;
            const isService = report.serviceTestRating === rating;
            return (
              <tr key={rating}>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">{TEST_RATING_LABEL[rating]}</td>
                <td
                  className={clsx(
                    'px-6 py-3 text-right text-sm',
                    isLending ? 'font-bold text-teal-700 bg-teal-50' : 'text-gray-600',
                  )}
                >
                  {POINT_TABLE[rating].lending}
                </td>
                <td
                  className={clsx(
                    'px-6 py-3 text-right text-sm',
                    isInvestment ? 'font-bold text-teal-700 bg-teal-50' : 'text-gray-600',
                  )}
                >
                  {POINT_TABLE[rating].investment}
                </td>
                <td
                  className={clsx(
                    'px-6 py-3 text-right text-sm',
                    isService ? 'font-bold text-teal-700 bg-teal-50' : 'text-gray-600',
                  )}
                >
                  {POINT_TABLE[rating].service}
                </td>
              </tr>
            );
          })}
          <tr className="bg-gray-50">
            <td className="px-6 py-3 text-sm font-semibold text-gray-900">Awarded</td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {report.lendingPoints ?? '—'}
            </td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {report.investmentPoints ?? '—'}
            </td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {report.servicePoints ?? '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="border-t bg-gray-50 px-6 py-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Composite bands</p>
        <div className="mt-2 grid grid-cols-4 gap-3">
          {COMPOSITE_BANDS.map((band) => (
            <div
              key={band.rating}
              className={clsx(
                'rounded border p-2 text-center',
                report.institutionRating === band.rating
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 bg-white',
              )}
            >
              <p className="text-xs font-medium text-gray-500">{band.label}</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{INSTITUTION_LABEL[band.rating]}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-sm text-gray-700">
          Composite:{' '}
          <span className="font-semibold">
            {report.compositePoints != null ? `${report.compositePoints} points` : 'not yet computed'}
          </span>
          {report.institutionRating && (
            <>
              {' '}
              → <span className="font-semibold">{INSTITUTION_LABEL[report.institutionRating]}</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function RecommendationsCard({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  const addMutation = useMutation({
    mutationFn: () => cradLargeBankApi.addRecommendation(teamId!, report.id, text),
    onSuccess: () => {
      setText('');
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
    onError: () => toast.error('Failed to add recommendation'),
  });

  const removeMutation = useMutation({
    mutationFn: (recId: string) => cradLargeBankApi.removeRecommendation(teamId!, report.id, recId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
  });

  return (
    <div className="rounded-lg border bg-white p-6">
      <h3 className="text-sm font-semibold text-gray-900">Recommendations</h3>
      <p className="mt-1 text-xs text-gray-500">
        Free-text recommendations that appear in the Memorandum and workbook export.
      </p>
      <div className="mt-4 space-y-2">
        {report.recommendations.length === 0 && (
          <p className="text-sm text-gray-500">No recommendations yet.</p>
        )}
        {report.recommendations.map((r) => (
          <div key={r.id} className="flex items-start gap-3 rounded border p-3">
            <span className="text-xs font-medium text-gray-500 mt-0.5">#{r.sequence}</span>
            <p className="flex-1 text-sm text-gray-800">{r.text}</p>
            <button
              onClick={() => removeMutation.mutate(r.id)}
              className="text-gray-400 hover:text-red-600"
              title="Remove"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Improve Lending Test performance in Louisiana and Texas…"
          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
        />
        <button
          onClick={() => text.trim() && addMutation.mutate()}
          disabled={!text.trim() || addMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4" />
          Add
        </button>
      </div>
    </div>
  );
}

function InvestmentTestPanel({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [avgTotalAssets, setAvgTotalAssets] = useState('');
  const [avgTotalSecurities, setAvgTotalSecurities] = useState('');

  const computeMutation = useMutation({
    mutationFn: () =>
      cradLargeBankApi.computeInvestmentTest(teamId!, report.id, {
        avgTotalAssets: avgTotalAssets ? Number(avgTotalAssets) : undefined,
        avgTotalSecurities: avgTotalSecurities ? Number(avgTotalSecurities) : undefined,
      }),
    onSuccess: () => {
      toast.success('Investment Test computed');
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
    onError: () => toast.error('Failed to compute Investment Test'),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900">Compute Investment Test</h3>
        <p className="mt-1 text-xs text-gray-500">
          Aggregates CD activities (INVESTMENT / GRANT / DONATION) by assessment area, splits current vs prior period using the prior exam date, and applies FDIC benchmarks. Total Assets + Total Securities are used to compute QI %TA / %TS at the institution level — analyst can leave blank to skip thresholding.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              Avg Total Assets ($)
            </label>
            <input
              type="number"
              value={avgTotalAssets}
              onChange={(e) => setAvgTotalAssets(e.target.value)}
              placeholder="2100000000"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              Avg Total Securities ($)
            </label>
            <input
              type="number"
              value={avgTotalSecurities}
              onChange={(e) => setAvgTotalSecurities(e.target.value)}
              placeholder="365000000"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => computeMutation.mutate()}
              disabled={computeMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
            >
              <CpuChipIcon className="h-4 w-4" />
              {computeMutation.isPending ? 'Computing…' : 'Compute'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Qualified Investments by AA</h3>
            <p className="text-xs text-gray-500">
              Current period (since prior exam) vs prior period vs grants/donations. Conclusions
              use FDIC vocab: Excellent / Significant / Adequate / Poor / Few, If Any.
            </p>
          </div>
          {report.investmentTestRating && (
            <StatusBadge variant={TEST_RATING_BADGE[report.investmentTestRating]}>
              Test: {TEST_RATING_LABEL[report.investmentTestRating]}
            </StatusBadge>
          )}
        </div>
        {report.qualifiedInvestments.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No qualified investments yet. Click "Compute" above after adding CD activities of type
            INVESTMENT, GRANT, or DONATION tied to assessment areas.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assessment Area
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prior #
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prior $
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current #
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current $
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grants #
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grants $
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conclusion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {report.qualifiedInvestments.map((q) => (
                <tr key={q.id}>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {findAaName(report, q.assessmentAreaId)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-700">
                    {q.priorInvestmentCount}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-700">
                    {formatMoney(q.priorInvestmentAmount)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-700">
                    {q.currentInvestmentCount}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-700">
                    {formatMoney(q.currentInvestmentAmount)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-700">
                    {q.grantsCount}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-700">
                    {formatMoney(q.grantsAmount)}
                  </td>
                  <td className="px-6 py-4">
                    {q.rating ? (
                      <StatusBadge variant={TEST_RATING_BADGE[q.rating]}>{q.conclusion}</StatusBadge>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function findAaName(report: LargeBankReport, aaId: string): string {
  const aa = report.assessmentAreas.find((a) => a.id === aaId);
  if (aa) return aa.name;
  // Fall back to a test-result join (some legacy rows may not be in the
  // assessmentAreas list if their AA was deleted after compute ran)
  const t = report.testResults.find((r) => r.assessmentAreaId === aaId);
  return t?.assessmentArea?.name ?? aaId.slice(0, 8);
}

function formatMoney(amount: number): string {
  if (amount === 0) return '$0';
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toFixed(0)}`;
}

function LendingTestPanel({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [avgTotalAssets, setAvgTotalAssets] = useState('');
  const [avgTotalLoans, setAvgTotalLoans] = useState('');

  // Build deposit input rows from existing state rating + test result AAs.
  // The analyst types in bank deposits per AA before computing.
  const aaIds = Array.from(
    new Set([
      ...report.qualifiedInvestments.map((q) => q.assessmentAreaId),
      ...report.testResults.map((t) => t.assessmentAreaId),
    ]),
  );
  const [deposits, setDeposits] = useState<Record<string, string>>({});

  const computeMutation = useMutation({
    mutationFn: () => {
      const depositsByAa = aaIds
        .map((aaId) => ({
          assessmentAreaId: aaId,
          bankDeposits: deposits[aaId] ? Number(deposits[aaId]) : 0,
        }))
        .filter((d) => d.bankDeposits > 0);
      return cradLargeBankApi.computeLendingTest(teamId!, report.id, {
        depositsByAa,
        avgTotalAssets: avgTotalAssets ? Number(avgTotalAssets) : undefined,
        avgTotalLoans: avgTotalLoans ? Number(avgTotalLoans) : undefined,
      });
    },
    onSuccess: () => {
      toast.success('Lending Test computed');
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Failed to compute Lending Test';
      toast.error(msg);
    },
  });

  const laRows = report.testResults.filter(
    (r) => r.test === 'LENDING' && r.subCriterion === 'LENDING_ACTIVITY',
  );
  const cdRows = report.testResults.filter(
    (r) => r.test === 'LENDING' && r.subCriterion === 'COMMUNITY_DEVELOPMENT_LOANS',
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900">Compute Lending Test</h3>
        <p className="mt-1 text-xs text-gray-500">
          Computes Lending Activity (bank loan share vs deposit share per AA) and Community
          Development Loans from CraCdActivity (type=LOAN). AA Concentration, Geographic
          Distribution, Borrower Profile, and Innovative & Flexible sub-criteria are covered by
          the existing CRAD Small Bank/ISB engine and the I&amp;F AI extraction service (next
          slice).
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              Avg Total Assets ($)
            </label>
            <input
              type="number"
              value={avgTotalAssets}
              onChange={(e) => setAvgTotalAssets(e.target.value)}
              placeholder="2100000000"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              Avg Total Loans ($)
            </label>
            <input
              type="number"
              value={avgTotalLoans}
              onChange={(e) => setAvgTotalLoans(e.target.value)}
              placeholder="1500000000"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
            />
          </div>
        </div>
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Bank Deposits per AA (FDIC SOD source) ($)
          </p>
          {aaIds.length === 0 ? (
            <p className="mt-2 text-xs text-gray-400">
              No assessment areas detected yet. Run Investment Test or Service Test first to
              populate AA references.
            </p>
          ) : (
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {aaIds.map((aaId) => (
                <div key={aaId} className="flex items-center gap-2">
                  <span className="flex-shrink-0 text-xs text-gray-500 font-mono w-32 truncate">
                    {aaId.slice(0, 12)}
                  </span>
                  <input
                    type="number"
                    value={deposits[aaId] ?? ''}
                    onChange={(e) =>
                      setDeposits((d) => ({ ...d, [aaId]: e.target.value }))
                    }
                    placeholder="0"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => computeMutation.mutate()}
            disabled={computeMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
          >
            <CpuChipIcon className="h-4 w-4" />
            {computeMutation.isPending ? 'Computing…' : 'Compute'}
          </button>
        </div>
      </div>

      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Lending Activity</h3>
            <p className="text-xs text-gray-500">
              Loan share ÷ deposit share per AA. ≥1.2 Excellent · ≥0.9 Good · ≥0.6 Adequate ·
              ≥0.3 Poor · &lt;0.3 Very Poor.
            </p>
          </div>
          {report.lendingTestRating && (
            <StatusBadge variant={TEST_RATING_BADGE[report.lendingTestRating]}>
              Test: {TEST_RATING_LABEL[report.lendingTestRating]}
            </StatusBadge>
          )}
        </div>
        {laRows.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No lending-activity rows yet. Click "Compute" after entering per-AA deposits.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SB
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  HMDA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SF
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CD
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % Loans
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % Deposits
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  L/D Ratio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conclusion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {laRows.map((r) => {
                const m = (r.metadata ?? {}) as {
                  smallBusinessLoanCount?: number;
                  smallFarmLoanCount?: number;
                  hmdaLoanCount?: number;
                  cdLoanCount?: number;
                  totalLoans?: number;
                  loanSharePct?: number;
                  depositSharePct?: number;
                  loanToDepositShareRatio?: number | null;
                };
                return (
                  <tr key={r.id}>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {findAaName(report, r.assessmentAreaId)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.smallBusinessLoanCount ?? 0}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.hmdaLoanCount ?? 0}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.smallFarmLoanCount ?? 0}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.cdLoanCount ?? 0}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                      {m.totalLoans ?? 0}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.loanSharePct != null ? `${(m.loanSharePct * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.depositSharePct != null ? `${(m.depositSharePct * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700 font-medium">
                      {m.loanToDepositShareRatio != null
                        ? m.loanToDepositShareRatio.toFixed(2)
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <EditableResultRating reportId={report.id} result={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Community Development Loans</h3>
          <p className="text-xs text-gray-500">
            Aggregated from CraCdActivity (type=LOAN) per AA. FDIC vocab: Leader / Relatively
            High / Adequate / Low / Few-If-Any.
          </p>
        </div>
        {cdRows.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">No CD loan rows yet.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total $
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conclusion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {cdRows.map((r) => {
                const m = (r.metadata ?? {}) as { count?: number; totalAmount?: number };
                return (
                  <tr key={r.id}>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {findAaName(report, r.assessmentAreaId)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">{m.count ?? 0}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {formatMoney(m.totalAmount ?? 0)}
                    </td>
                    <td className="px-6 py-4">
                      <EditableResultRating reportId={report.id} result={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <InnovativeFlexibleSection report={report} />
    </div>
  );
}

function InnovativeFlexibleSection({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();

  const extractMutation = useMutation({
    mutationFn: () => cradLargeBankApi.extractIf(teamId!, report.id),
    onSuccess: () => {
      toast.success('AI extraction queued — refresh in 20-60s');
    },
    onError: () => toast.error('Failed to start AI extraction'),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      suggestionId,
      body,
    }: {
      suggestionId: string;
      body: {
        status?: 'PENDING' | 'ACCEPTED' | 'EDITED' | 'REJECTED';
        editedName?: string;
        editedRationale?: string;
        proposedRating?: string;
      };
    }) => cradLargeBankApi.updateIfSuggestion(teamId!, report.id, suggestionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
    onError: () => toast.error('Failed to update suggestion'),
  });

  const deleteMutation = useMutation({
    mutationFn: (suggestionId: string) =>
      cradLargeBankApi.deleteIfSuggestion(teamId!, report.id, suggestionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
  });

  const pending = report.ifSuggestions.filter((s) => s.status === 'PENDING');
  const ratified = report.ifSuggestions.filter((s) => s.status === 'ACCEPTED' || s.status === 'EDITED');
  const rejected = report.ifSuggestions.filter((s) => s.status === 'REJECTED');

  return (
    <div className="rounded-lg border bg-white">
      <div className="border-b px-6 py-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <SparklesIcon className="h-4 w-4 text-teal-600" />
            Innovative &amp; Flexible Lending Practices
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            AI extracts candidate programs from your LENDING + DEPOSITS policies, scoring each
            for likely FDIC I&amp;F qualification. Review, edit, or reject each suggestion
            before it appears in the workbook + memorandum.
          </p>
        </div>
        <button
          onClick={() => extractMutation.mutate()}
          disabled={extractMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
        >
          <SparklesIcon className="h-4 w-4" />
          {extractMutation.isPending ? 'Queueing…' : 'Run AI extraction'}
        </button>
      </div>

      <div className="px-6 py-4 space-y-4">
        {report.ifSuggestions.length === 0 && (
          <p className="text-sm text-gray-500">
            No programs extracted yet. Click "Run AI extraction" to scan policies; ensure your
            LENDING + DEPOSITS policies have content first.
          </p>
        )}

        {pending.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Pending review ({pending.length})
            </h4>
            <div className="space-y-2">
              {pending.map((s) => (
                <IfSuggestionCard
                  key={s.id}
                  suggestion={s}
                  onUpdate={(body) => updateMutation.mutate({ suggestionId: s.id, body })}
                  onDelete={() => deleteMutation.mutate(s.id)}
                />
              ))}
            </div>
          </div>
        )}

        {ratified.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Ratified ({ratified.length})
            </h4>
            <div className="space-y-2">
              {ratified.map((s) => (
                <IfSuggestionCard
                  key={s.id}
                  suggestion={s}
                  onUpdate={(body) => updateMutation.mutate({ suggestionId: s.id, body })}
                  onDelete={() => deleteMutation.mutate(s.id)}
                />
              ))}
            </div>
          </div>
        )}

        {rejected.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Rejected ({rejected.length})
            </h4>
            <div className="space-y-2">
              {rejected.map((s) => (
                <IfSuggestionCard
                  key={s.id}
                  suggestion={s}
                  onUpdate={(body) => updateMutation.mutate({ suggestionId: s.id, body })}
                  onDelete={() => deleteMutation.mutate(s.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IfSuggestionCard({
  suggestion,
  onUpdate,
  onDelete,
}: {
  suggestion: IfSuggestion;
  onUpdate: (body: {
    status?: 'PENDING' | 'ACCEPTED' | 'EDITED' | 'REJECTED';
    editedName?: string;
    editedRationale?: string;
    proposedRating?: TestRating;
  }) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(suggestion.editedName ?? suggestion.programName);
  const [rationale, setRationale] = useState(
    suggestion.editedRationale ?? suggestion.rationale,
  );
  const [rating, setRating] = useState<TestRating | null>(suggestion.proposedRating);

  const isRatified = suggestion.status === 'ACCEPTED' || suggestion.status === 'EDITED';
  const isRejected = suggestion.status === 'REJECTED';
  const isPending = suggestion.status === 'PENDING';
  const confidencePct = Math.round(suggestion.confidence * 100);

  return (
    <div
      className={clsx(
        'rounded border p-3',
        isRatified ? 'border-teal-200 bg-teal-50/30' : isRejected ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200',
      )}
    >
      {editing ? (
        <div className="space-y-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm font-medium"
          />
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
          />
          <div className="flex items-center gap-2">
            <select
              value={rating ?? ''}
              onChange={(e) => setRating((e.target.value || null) as TestRating | null)}
              className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
            >
              <option value="">— rating —</option>
              {TEST_RATING_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {TEST_RATING_LABEL[r]}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                onUpdate({
                  status: 'EDITED',
                  editedName: name,
                  editedRationale: rationale,
                  proposedRating: rating ?? undefined,
                });
                setEditing(false);
              }}
              className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-500"
            >
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-gray-900">
                  {suggestion.editedName ?? suggestion.programName}
                </p>
                {suggestion.programCategory && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    {suggestion.programCategory}
                  </span>
                )}
                {suggestion.proposedRating && (
                  <StatusBadge variant={TEST_RATING_BADGE[suggestion.proposedRating]}>
                    {TEST_RATING_LABEL[suggestion.proposedRating]}
                  </StatusBadge>
                )}
                <span className="text-xs text-gray-500">{confidencePct}% confidence</span>
              </div>
              <p className="mt-1 text-sm text-gray-700">
                {suggestion.editedRationale ?? suggestion.rationale}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {isPending && (
                <>
                  <button
                    onClick={() => onUpdate({ status: 'ACCEPTED' })}
                    title="Accept"
                    className="p-1.5 text-teal-700 hover:bg-teal-100 rounded"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditing(true)}
                    title="Edit"
                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onUpdate({ status: 'REJECTED' })}
                    title="Reject"
                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </>
              )}
              {isRatified && (
                <button
                  onClick={() => onUpdate({ status: 'PENDING' })}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Move back to pending
                </button>
              )}
              {isRejected && (
                <button
                  onClick={() => onUpdate({ status: 'PENDING' })}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Restore
                </button>
              )}
              <button
                onClick={onDelete}
                title="Delete"
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceTestPanel({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();

  const computeMutation = useMutation({
    mutationFn: () => cradLargeBankApi.computeServiceTest(teamId!, report.id),
    onSuccess: () => {
      toast.success('Service Test computed');
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
    onError: () => toast.error('Failed to compute Service Test'),
  });

  const branchRows = report.testResults.filter(
    (r) => r.test === 'SERVICE' && r.subCriterion === 'DISTRIBUTION_OF_BRANCHES',
  );
  const cdRows = report.testResults.filter(
    (r) => r.test === 'SERVICE' && r.subCriterion === 'COMMUNITY_DEVELOPMENT_SERVICES',
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Compute Service Test</h3>
            <p className="mt-1 text-xs text-gray-500">
              Computes Distribution of Branches (bank LMI branch share vs AA LMI population
              share) and Community Development Services from CraCdActivity (type=SERVICE) per AA.
              Reasonableness of Business Hours and Branch Changes sub-criteria are qualitative
              and unlocked from the Component & Composite tab.
            </p>
          </div>
          <button
            onClick={() => computeMutation.mutate()}
            disabled={computeMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
          >
            <CpuChipIcon className="h-4 w-4" />
            {computeMutation.isPending ? 'Computing…' : 'Compute'}
          </button>
        </div>
      </div>

      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Distribution of Branches</h3>
            <p className="text-xs text-gray-500">
              Coverage ratio = bank LMI branch % ÷ AA LMI population %. ≥1.2 Readily Accessible
              · ≥0.9 Accessible · ≥0.7 Reasonably · ≥0.4 Limited · &lt;0.4 Significant.
            </p>
          </div>
          {report.serviceTestRating && (
            <StatusBadge variant={TEST_RATING_BADGE[report.serviceTestRating]}>
              Test: {TEST_RATING_LABEL[report.serviceTestRating]}
            </StatusBadge>
          )}
        </div>
        {branchRows.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No branch-distribution rows yet. Click "Compute" once branches are enriched with
            tract income tier (BranchLocation.craIncomeLevel).
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Branches
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  L / M / Mid / Up / Unk
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bank LMI %
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AA LMI Pop %
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conclusion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {branchRows.map((r) => {
                const m = (r.metadata ?? {}) as {
                  totalBranches?: number;
                  branchesByIncome?: Record<string, number>;
                  bankLmiBranchSharePct?: number | null;
                  aaLmiPopulationSharePct?: number | null;
                  coverageRatio?: number | null;
                };
                const bi = m.branchesByIncome ?? {};
                return (
                  <tr key={r.id}>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {findAaName(report, r.assessmentAreaId)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">{m.totalBranches ?? 0}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600 font-mono">
                      {`${bi.low ?? 0} / ${bi.moderate ?? 0} / ${bi.middle ?? 0} / ${bi.upper ?? 0} / ${bi.unknown ?? 0}`}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.bankLmiBranchSharePct != null
                        ? `${(m.bankLmiBranchSharePct * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.aaLmiPopulationSharePct != null
                        ? `${(m.aaLmiPopulationSharePct * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {m.coverageRatio != null ? m.coverageRatio.toFixed(2) : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <EditableResultRating reportId={report.id} result={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Community Development Services</h3>
          <p className="text-xs text-gray-500">
            Aggregated from CraCdActivity (type=SERVICE) per AA. Conclusions use FDIC vocab:
            Leader / Relatively High / Adequate / Limited / Few, If Any.
          </p>
        </div>
        {cdRows.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No CD service rows yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conclusion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {cdRows.map((r) => {
                const m = (r.metadata ?? {}) as { serviceCount?: number; serviceHoursTotal?: number };
                return (
                  <tr key={r.id}>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {findAaName(report, r.assessmentAreaId)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">{m.serviceCount ?? 0}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">
                      {(m.serviceHoursTotal ?? 0).toFixed(0)}
                    </td>
                    <td className="px-6 py-4">
                      <EditableResultRating reportId={report.id} result={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MemorandumPanel({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [enrichWithAi, setEnrichWithAi] = useState(false);

  const generateMutation = useMutation({
    mutationFn: () => cradLargeBankApi.generateMemorandum(teamId!, report.id, { enrichWithAi }),
    onSuccess: () => {
      toast.success(
        enrichWithAi ? 'Memorandum generated with AI enrichment' : 'Memorandum generated',
      );
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
    onError: () => toast.error('Failed to generate memorandum'),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">Generate Memorandum</h3>
            <p className="mt-1 text-xs text-gray-500">
              Builds the long-form examiner-handoff narrative from the persisted report data.
              The deterministic version works without AI; toggle "AI enrichment" to add prose
              polish (every number and conclusion preserved verbatim).
            </p>
            <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={enrichWithAi}
                onChange={(e) => setEnrichWithAi(e.target.checked)}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span>AI enrichment (slower, formal-regulatory tone)</span>
            </label>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
            >
              <DocumentTextIcon className="h-4 w-4" />
              {generateMutation.isPending ? 'Generating…' : 'Generate'}
            </button>
            {report.memorandumText && (
              <>
                <a
                  href={cradLargeBankApi.memorandumPdfUrl(teamId!, report.id)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download PDF
                </a>
                <a
                  href={cradLargeBankApi.memorandumMarkdownUrl(teamId!, report.id)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Markdown
                </a>
              </>
            )}
          </div>
        </div>
        {report.memorandumGeneratedAt && (
          <p className="mt-3 text-xs text-gray-500">
            Last generated: {new Date(report.memorandumGeneratedAt).toLocaleString()}
          </p>
        )}
      </div>

      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Preview</h3>
        </div>
        {report.memorandumText ? (
          <div className="px-6 py-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
              {report.memorandumText}
            </pre>
          </div>
        ) : (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3">
              No memorandum generated yet. Click "Generate" once your tests have computed and
              recommendations are entered.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function PerAaDetailPanel({ report }: { report: LargeBankReport }) {
  const aas = report.assessmentAreas;
  const [selectedId, setSelectedId] = useState<string | null>(aas[0]?.id ?? null);

  if (aas.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-12 text-center">
        <ScaleIcon className="mx-auto h-10 w-10 text-gray-300" />
        <h3 className="mt-4 text-sm font-semibold text-gray-900">No assessment areas yet</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
          Run one of the test computes (Lending, Investment, or Service) to attach assessment
          areas to this report. AAs come from your team's CRA module.
        </p>
      </div>
    );
  }

  const selected = aas.find((a) => a.id === selectedId) ?? aas[0];

  return (
    <div className="flex gap-4">
      <div className="w-64 flex-shrink-0">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Assessment Areas ({aas.length})
        </h3>
        <div className="space-y-1">
          {aas.map((aa) => (
            <button
              key={aa.id}
              onClick={() => setSelectedId(aa.id)}
              className={clsx(
                'block w-full text-left rounded px-3 py-2 text-sm transition-colors',
                selected.id === aa.id
                  ? 'bg-teal-50 text-teal-900 border border-teal-200'
                  : 'text-gray-700 hover:bg-gray-50',
              )}
            >
              <p className="font-medium">{aa.name}</p>
              {(aa.stateName || aa.multistateMsaName) && (
                <p className="text-xs text-gray-500">
                  {aa.multistateMsaName ?? aa.stateName}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-w-0 space-y-4">
        <AaDemographicsCard aa={selected} />
        <AaTestResultsCard report={report} aaId={selected.id} />
        <AaCompetitionCard />
      </div>
    </div>
  );
}

function AaDemographicsCard({ aa }: { aa: AssessmentArea }) {
  const pop = aa.populationByIncome ?? {};
  const housing = aa.housingByIncome ?? {};

  return (
    <div className="rounded-lg border bg-white">
      <div className="border-b px-6 py-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{aa.name}</h3>
          <div className="text-xs text-gray-500">
            {aa.multistateMsaName
              ? `${aa.multistateMsaName} (multistate MSA)`
              : aa.stateName ?? '—'}
            {aa.referenceYear && ` · ${aa.referenceYear}`}
            {aa.mmctMethodology && ` · ${aa.mmctMethodology}`}
          </div>
        </div>
        {aa.description && <p className="mt-1 text-xs text-gray-500">{aa.description}</p>}
      </div>
      <div className="px-6 py-4 grid gap-6 md:grid-cols-2">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Demographics
          </h4>
          <dl className="space-y-1.5 text-sm">
            <Row label="Total Tracts" value={aa.totalTracts.toLocaleString()} />
            <Row label="LMI Tracts" value={`${aa.lmiTracts.toLocaleString()} (${aa.totalTracts > 0 ? ((aa.lmiTracts / aa.totalTracts) * 100).toFixed(1) : '—'}%)`} />
            <Row
              label="Majority-Minority Tracts"
              value={aa.majorityMinority.toLocaleString()}
            />
            <Row label="Population" value={aa.totalPopulation.toLocaleString()} />
            <Row
              label="LMI Population %"
              value={aa.lmiPopulationPct != null ? `${(aa.lmiPopulationPct * 100).toFixed(1)}%` : '—'}
            />
            <Row
              label="Minority Population %"
              value={
                aa.minorityPopulationPct != null
                  ? `${(aa.minorityPopulationPct * 100).toFixed(1)}%`
                  : '—'
              }
            />
            <Row
              label="Median Family Income"
              value={
                aa.medianFamilyIncome != null
                  ? `$${aa.medianFamilyIncome.toLocaleString()}${aa.medianFamilyIncomeYear ? ` (${aa.medianFamilyIncomeYear})` : ''}`
                  : '—'
              }
            />
          </dl>
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Housing Stock
          </h4>
          <dl className="space-y-1.5 text-sm">
            <Row label="Housing Units" value={aa.housingUnits?.toLocaleString() ?? '—'} />
            <Row
              label="Owner-Occupied"
              value={aa.ownerOccupiedUnits?.toLocaleString() ?? '—'}
            />
            <Row label="Rental" value={aa.rentalUnits?.toLocaleString() ?? '—'} />
            <Row label="Vacant" value={aa.vacantUnits?.toLocaleString() ?? '—'} />
          </dl>
        </div>
      </div>

      {(Object.keys(pop).length > 0 || Object.keys(housing).length > 0) && (
        <div className="border-t px-6 py-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Distribution by Tract Income Tier
          </h4>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left font-medium py-1">Tier</th>
                <th className="text-right font-medium py-1">Population %</th>
                <th className="text-right font-medium py-1">Owner-Occupied %</th>
              </tr>
            </thead>
            <tbody>
              {(['low', 'moderate', 'middle', 'upper', 'unknown'] as const).map((tier) => (
                <tr key={tier} className="border-t">
                  <td className="py-1.5 capitalize">{tier}</td>
                  <td className="py-1.5 text-right">
                    {pop[tier]?.pct != null ? `${((pop[tier]?.pct ?? 0) * 100).toFixed(1)}%` : '—'}
                  </td>
                  <td className="py-1.5 text-right">
                    {housing[tier]?.pct != null
                      ? `${((housing[tier]?.pct ?? 0) * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AaTestResultsCard({ report, aaId }: { report: LargeBankReport; aaId: string }) {
  const rows = report.testResults.filter((r) => r.assessmentAreaId === aaId);
  const qi = report.qualifiedInvestments.find((q) => q.assessmentAreaId === aaId);

  const grouped: Record<'LENDING' | 'INVESTMENT' | 'SERVICE', TestResult[]> = {
    LENDING: rows.filter((r) => r.test === 'LENDING'),
    INVESTMENT: rows.filter((r) => r.test === 'INVESTMENT'),
    SERVICE: rows.filter((r) => r.test === 'SERVICE'),
  };

  return (
    <div className="rounded-lg border bg-white">
      <div className="border-b px-6 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Test Results for this AA</h3>
        <p className="text-xs text-gray-500">
          Sub-criterion conclusions populated by the per-test compute services. Empty rows
          mean that sub-criterion hasn't been computed for this AA yet.
        </p>
      </div>
      <div className="divide-y">
        {(['LENDING', 'INVESTMENT', 'SERVICE'] as const).map((test) => {
          const testRows = grouped[test];
          return (
            <div key={test} className="px-6 py-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                {test === 'LENDING'
                  ? 'Lending Test'
                  : test === 'INVESTMENT'
                    ? 'Investment Test'
                    : 'Service Test'}
              </h4>
              {testRows.length === 0 ? (
                <p className="text-xs text-gray-400">Not yet computed for this AA.</p>
              ) : (
                <div className="space-y-2">
                  {testRows.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-start justify-between gap-3 rounded border border-gray-100 bg-gray-50 px-3 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700">
                          {formatSubCriterion(r.subCriterion)}
                          <span className="ml-2 text-xs text-gray-400 font-normal">
                            {r.reviewScope === 'LIMITED_SCOPE' ? '(Limited Scope)' : '(Full Scope)'}
                          </span>
                        </p>
                        {r.metadata && (
                          <SubCriterionMetadata
                            subCriterion={r.subCriterion}
                            metadata={r.metadata}
                          />
                        )}
                      </div>
                      <EditableResultRating reportId={report.id} result={r} />
                    </div>
                  ))}
                  {test === 'INVESTMENT' && qi && (
                    <div className="rounded border border-gray-100 bg-white px-3 py-2 text-xs text-gray-600">
                      <p className="font-medium text-gray-700">Qualified Investments detail</p>
                      <p>
                        Current: {qi.currentInvestmentCount} for{' '}
                        {formatMoney(qi.currentInvestmentAmount)}
                        {' · '}Prior: {qi.priorInvestmentCount} for{' '}
                        {formatMoney(qi.priorInvestmentAmount)}
                        {' · '}Grants: {qi.grantsCount} for {formatMoney(qi.grantsAmount)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AaCompetitionCard() {
  return (
    <div className="rounded-lg border border-dashed bg-gray-50 px-6 py-4 text-sm text-gray-500">
      <p className="font-medium text-gray-700">Competition data (FDIC SOD + CRA aggregate)</p>
      <p className="mt-1 text-xs">
        Per-AA deposit market share, SB market share, and HMDA market share land in Phase C
        once the FDIC SOD ingest + FFIEC aggregate pipelines are wired. For now the analyst
        references the same data from the existing CRAD Small Bank/ISB output.
      </p>
    </div>
  );
}

function formatSubCriterion(s: string): string {
  switch (s) {
    case 'LENDING_ACTIVITY':
      return 'Lending Activity';
    case 'AA_CONCENTRATION':
      return 'AA Concentration';
    case 'GEOGRAPHIC_DISTRIBUTION':
      return 'Geographic Distribution';
    case 'BORROWER_PROFILE':
      return "Borrower's Profile";
    case 'COMMUNITY_DEVELOPMENT_LOANS':
      return 'Community Development Loans';
    case 'INNOVATIVE_FLEXIBLE':
      return 'Innovative & Flexible';
    case 'QUALIFIED_INVESTMENTS':
      return 'Qualified Investments';
    case 'DISTRIBUTION_OF_BRANCHES':
      return 'Distribution of Branches';
    case 'COMMUNITY_DEVELOPMENT_SERVICES':
      return 'CD Services';
    default:
      return s
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

function SubCriterionMetadata({
  subCriterion,
  metadata,
}: {
  subCriterion: string;
  metadata: Record<string, unknown>;
}) {
  if (subCriterion === 'LENDING_ACTIVITY') {
    const m = metadata as {
      smallBusinessLoanCount?: number;
      smallFarmLoanCount?: number;
      hmdaLoanCount?: number;
      cdLoanCount?: number;
      totalLoans?: number;
      loanSharePct?: number;
      depositSharePct?: number;
      loanToDepositShareRatio?: number | null;
    };
    return (
      <p className="text-xs text-gray-500 mt-0.5">
        {m.totalLoans ?? 0} loans (SB {m.smallBusinessLoanCount ?? 0} · HMDA{' '}
        {m.hmdaLoanCount ?? 0} · SF {m.smallFarmLoanCount ?? 0} · CD {m.cdLoanCount ?? 0}) ·
        L/D ratio {m.loanToDepositShareRatio?.toFixed(2) ?? '—'}
      </p>
    );
  }
  if (subCriterion === 'COMMUNITY_DEVELOPMENT_LOANS') {
    const m = metadata as { count?: number; totalAmount?: number };
    return (
      <p className="text-xs text-gray-500 mt-0.5">
        {m.count ?? 0} loans · {formatMoney(m.totalAmount ?? 0)}
      </p>
    );
  }
  if (subCriterion === 'DISTRIBUTION_OF_BRANCHES') {
    const m = metadata as {
      totalBranches?: number;
      bankLmiBranchSharePct?: number | null;
      aaLmiPopulationSharePct?: number | null;
      coverageRatio?: number | null;
    };
    return (
      <p className="text-xs text-gray-500 mt-0.5">
        {m.totalBranches ?? 0} branches · bank LMI {m.bankLmiBranchSharePct != null ? `${(m.bankLmiBranchSharePct * 100).toFixed(0)}%` : '—'} vs AA LMI {m.aaLmiPopulationSharePct != null ? `${(m.aaLmiPopulationSharePct * 100).toFixed(0)}%` : '—'} · ratio {m.coverageRatio?.toFixed(2) ?? '—'}
      </p>
    );
  }
  if (subCriterion === 'COMMUNITY_DEVELOPMENT_SERVICES') {
    const m = metadata as { serviceCount?: number; serviceHoursTotal?: number };
    return (
      <p className="text-xs text-gray-500 mt-0.5">
        {m.serviceCount ?? 0} services · {(m.serviceHoursTotal ?? 0).toFixed(0)} hours
      </p>
    );
  }
  if (subCriterion === 'QUALIFIED_INVESTMENTS') {
    const m = metadata as {
      currentInvestmentCount?: number;
      currentInvestmentAmount?: number;
      priorInvestmentCount?: number;
      priorInvestmentAmount?: number;
      grantsCount?: number;
      grantsAmount?: number;
    };
    return (
      <p className="text-xs text-gray-500 mt-0.5">
        Current {m.currentInvestmentCount ?? 0} for {formatMoney(m.currentInvestmentAmount ?? 0)} ·
        Prior {m.priorInvestmentCount ?? 0} for {formatMoney(m.priorInvestmentAmount ?? 0)} · Grants{' '}
        {m.grantsCount ?? 0}
      </p>
    );
  }
  return null;
}

function PeerComparisonPanel({ report }: { report: LargeBankReport }) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [peerName, setPeerName] = useState('');
  const [cdLoansSummary, setCdLoansSummary] = useState('');
  const [qualifiedInvestmentsSummary, setQualifiedInvestmentsSummary] = useState('');
  const [cdServicesSummary, setCdServicesSummary] = useState('');

  const addMutation = useMutation({
    mutationFn: () =>
      cradLargeBankApi.addPeerBenchmark(teamId!, report.id, {
        peerName,
        cdLoansSummary: cdLoansSummary || undefined,
        qualifiedInvestmentsSummary: qualifiedInvestmentsSummary || undefined,
        cdServicesSummary: cdServicesSummary || undefined,
      }),
    onSuccess: () => {
      setPeerName('');
      setCdLoansSummary('');
      setQualifiedInvestmentsSummary('');
      setCdServicesSummary('');
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
    onError: () => toast.error('Failed to add peer'),
  });

  const removeMutation = useMutation({
    mutationFn: (peerId: string) => cradLargeBankApi.removePeerBenchmark(teamId!, report.id, peerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, report.id] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white">
        <div className="border-b px-6 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Peer Benchmarks</h3>
          <p className="text-xs text-gray-500">
            Manual entry at MVP per spec §10.3. Auto-extraction from PE PDFs lands once Sprint 9
            CRA PE pipeline ships.
          </p>
        </div>
        {report.peerBenchmarks.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">No peers added yet.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Peer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CD Loans
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qualified Investments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CD Services
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {report.peerBenchmarks.map((p) => (
                <tr key={p.id}>
                  <td className="px-6 py-4 text-sm">
                    <p className="font-medium text-gray-900">{p.peerName}</p>
                    {p.peerCertNumber && <p className="text-xs text-gray-500">Cert #{p.peerCertNumber}</p>}
                    {p.peerExamDate && (
                      <p className="text-xs text-gray-500">
                        Exam: {new Date(p.peerExamDate).toLocaleDateString()}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">{p.cdLoansSummary ?? '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                    {p.qualifiedInvestmentsSummary ?? '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">{p.cdServicesSummary ?? '—'}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => removeMutation.mutate(p.id)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-gray-900">Add peer</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <input
            type="text"
            value={peerName}
            onChange={(e) => setPeerName(e.target.value)}
            placeholder="Peer name (required)"
            className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
          <input
            type="text"
            value={cdLoansSummary}
            onChange={(e) => setCdLoansSummary(e.target.value)}
            placeholder="CD Loans summary (e.g., 65 loans for $64.8M)"
            className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
          <input
            type="text"
            value={qualifiedInvestmentsSummary}
            onChange={(e) => setQualifiedInvestmentsSummary(e.target.value)}
            placeholder="Qualified Investments summary"
            className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
          <input
            type="text"
            value={cdServicesSummary}
            onChange={(e) => setCdServicesSummary(e.target.value)}
            placeholder="CD Services summary"
            className="rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => peerName.trim() && addMutation.mutate()}
            disabled={!peerName.trim() || addMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" />
            Add peer
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Small helpers ───────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900">{value}</dd>
    </div>
  );
}

/**
 * Per-AA test result rating with click-to-edit.
 *
 * Shows the rating badge by default; clicking switches to an inline
 * dropdown that PATCHes the test result on change. Used in all three
 * per-test tabs (Lending / Investment / Service).
 */
function EditableResultRating({
  reportId,
  result,
}: {
  reportId: string;
  result: TestResult;
}) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (body: { rating: TestRating | null }) =>
      cradLargeBankApi.updateTestResult(teamId!, reportId, result.id, body),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank', teamId, reportId] });
    },
    onError: () => toast.error('Failed to update rating'),
  });

  if (editing) {
    return (
      <select
        autoFocus
        value={result.rating ?? ''}
        onBlur={() => setEditing(false)}
        onChange={(e) =>
          updateMutation.mutate({ rating: (e.target.value || null) as TestRating | null })
        }
        className="rounded border border-gray-300 text-xs px-2 py-1 focus:border-teal-500 focus:ring-teal-500"
      >
        <option value="">—</option>
        {TEST_RATING_OPTIONS.map((r) => (
          <option key={r} value={r}>
            {TEST_RATING_LABEL[r]}
          </option>
        ))}
      </select>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      title="Click to override rating"
      className="text-left hover:opacity-80"
    >
      {result.rating ? (
        <StatusBadge variant={TEST_RATING_BADGE[result.rating]}>
          {result.conclusion ?? TEST_RATING_LABEL[result.rating]}
        </StatusBadge>
      ) : (
        <span className="text-xs text-gray-400 underline-offset-2 hover:underline">
          Add rating
        </span>
      )}
    </button>
  );
}

function RatingSelect({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: TestRating | null;
  onChange: (rating: TestRating | null) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange((e.target.value || null) as TestRating | null)}
        disabled={disabled}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm disabled:opacity-50"
      >
        <option value="">— not yet rated —</option>
        {TEST_RATING_OPTIONS.map((r) => (
          <option key={r} value={r}>
            {TEST_RATING_LABEL[r]}
          </option>
        ))}
      </select>
    </div>
  );
}
